import type { MiningCheckpoint, MiningCheckpointStore, MiningCheckpointWrite } from "./types";

const DATABASE_NAME = "pons-forest-terminal";
const DATABASE_VERSION = 1;
const CHECKPOINT_STORE = "mining-checkpoints";
export const LEASE_STORE = "mining-leases";

export class IndexedDbMiningCheckpointStore implements MiningCheckpointStore {
  async load(key: string): Promise<MiningCheckpoint | null> {
    const database = await openMiningDatabase();
    try {
      return await requestResult<MiningCheckpoint | undefined>(
        database.transaction(CHECKPOINT_STORE, "readonly").objectStore(CHECKPOINT_STORE).get(key),
      ) ?? null;
    } finally {
      database.close();
    }
  }

  async list(): Promise<MiningCheckpoint[]> {
    const database = await openMiningDatabase();
    try {
      return await requestResult<MiningCheckpoint[]>(
        database.transaction(CHECKPOINT_STORE, "readonly").objectStore(CHECKPOINT_STORE).getAll());
    } finally { database.close(); }
  }

  async save(checkpoint: MiningCheckpoint, write: MiningCheckpointWrite): Promise<void> {
    validateCheckpoint(checkpoint);
    const value = structuredClone(checkpoint);
    const database = await openMiningDatabase();
    try {
      const transaction = database.transaction([CHECKPOINT_STORE, LEASE_STORE], "readwrite");
      const done = transactionDone(transaction);
      const store = transaction.objectStore(CHECKPOINT_STORE);
      // Read both authorities in the same serialized transaction as the put.
      try {
        const [previous, lease] = await Promise.all([
          requestResult<MiningCheckpoint | undefined>(store.get(value.key)),
          requestResult<{ ownerId: string; generation: number; expiresAtMs: number } | undefined>(
            transaction.objectStore(LEASE_STORE).get(value.key)),
        ]);
        assertWrite(previous ?? null, value, write);
        const owner = write.fence?.indexedDb;
        if (owner && (!lease || lease.ownerId !== owner.ownerId || lease.generation !== owner.generation ||
          lease.expiresAtMs <= Date.now())) throw new Error("MINING_TAB_LEASE_LOST");
        value.revision = (previous?.revision ?? 0) + 1;
        store.put(value);
        await done;
      } catch (error) {
        try { transaction.abort(); } catch { /* A request failure may already have aborted it. */ }
        await done.catch(() => undefined);
        throw error;
      }
      checkpoint.revision = value.revision;
    } finally {
      database.close();
    }
  }

  async remove(key: string): Promise<void> {
    const database = await openMiningDatabase();
    try {
      const transaction = database.transaction(CHECKPOINT_STORE, "readwrite");
      transaction.objectStore(CHECKPOINT_STORE).delete(key);
      await transactionDone(transaction);
    } finally {
      database.close();
    }
  }
}

/** In-memory test store; omitting the write guard is reserved for fixture seeding. */
export class MemoryMiningCheckpointStore implements MiningCheckpointStore {
  private readonly values = new Map<string, MiningCheckpoint>();
  async load(key: string): Promise<MiningCheckpoint | null> {
    const value = this.values.get(key);
    return value ? structuredClone(value) : null;
  }
  async list(): Promise<MiningCheckpoint[]> { return structuredClone([...this.values.values()]); }
  async save(checkpoint: MiningCheckpoint, write?: MiningCheckpointWrite): Promise<void> {
    validateCheckpoint(checkpoint);
    const previous = this.values.get(checkpoint.key) ?? null;
    assertWrite(previous, checkpoint, write);
    const value = structuredClone(checkpoint);
    value.revision = (previous?.revision ?? 0) + 1;
    this.values.set(checkpoint.key, value);
    checkpoint.revision = value.revision;
  }
  async remove(key: string): Promise<void> { this.values.delete(key); }
}

export async function openMiningDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") throw new Error("INDEXED_DB_UNAVAILABLE");
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CHECKPOINT_STORE)) {
        database.createObjectStore(CHECKPOINT_STORE, { keyPath: "key" });
      }
      if (!database.objectStoreNames.contains(LEASE_STORE)) {
        database.createObjectStore(LEASE_STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("INDEXED_DB_OPEN_FAILED"));
    request.onblocked = () => reject(new Error("INDEXED_DB_UPGRADE_BLOCKED"));
  });
}

export function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("INDEXED_DB_REQUEST_FAILED"));
  });
}

export function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("INDEXED_DB_TRANSACTION_FAILED"));
    transaction.onabort = () => reject(transaction.error ?? new Error("INDEXED_DB_TRANSACTION_ABORTED"));
  });
}

function validateCheckpoint(value: MiningCheckpoint): void {
  if (value.version !== 1 || !value.key || !/^(0|[1-9][0-9]*)$/.test(value.reservedThroughExclusive) ||
    !/^(0|[1-9][0-9]*)$/.test(value.completedAttempts) || !Number.isFinite(value.activeElapsedMs) ||
    value.activeElapsedMs < 0 || !Number.isFinite(value.updatedAtMs) ||
    value.revision !== undefined && (!Number.isSafeInteger(value.revision) || value.revision < 0)) {
    throw new Error("INVALID_MINING_CHECKPOINT");
  }
}

function assertWrite(previous: MiningCheckpoint | null, value: MiningCheckpoint, write?: MiningCheckpointWrite): void {
  if (!write) return;
  if ((previous ? previous.revision ?? 0 : null) !== write.expectedRevision) {
    throw new Error("MINING_CHECKPOINT_VERSION_CHANGED");
  }
  if (write.fence && (write.fence.key !== value.key || !write.fence.isCurrent())) {
    throw new Error("MINING_TAB_LEASE_LOST");
  }
}
